import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  CollapseSection,
  Modal,
  useLocalStorageAsState,
  useScreenWidth,
} from "@msys/ui";
import {
  Add as AddIcon,
  DeleteOutlined as DeleteOutlineIcon,
} from "@mui/icons-material";
import { Box, Button, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, Form, Formik } from "formik";
import { noop, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  type DocType,
  ItemType,
  ModifyItemRecommendedTemplateValuesInput,
  PermissionName,
  Props2,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import { type DocumentByType } from "../../auth/RestrictedByDocumentPermissionByType.js";
import { FullScreenToggleIconButton } from "../../commons/button/FullScreenToggleIconButton.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import {
  ExpressionInputWithVariables,
  type VariableInputState,
  type ExpressionItemSource,
} from "../doc-items/modals/ExpressionInputWithVariables.js";
import { useExpressionValidation } from "../doc-items/modals/useExpressionValidation.js";
import {
  getEnhancedPropertyLabel,
  getPropertyType,
  isArrayType,
  isBooleanType,
  isNonComputedProp,
  isNotArrayType,
  isNumberType,
  isTextType,
} from "../doc-items/properties.js";
import {
  EditRecommendedTemplateModal_ItemRecommendationConfigFragment,
  EditRecommendedTemplateModal_ItemRecommendationFragment,
  EditRecommendedTemplateModal_RecommendedTemplateConfigFragment,
  useEditRecommendedTemplateModal__LatestAvailableVersionQuery,
  useEditRecommendedTemplateModalQuery,
} from "./EditRecommendedTemplateModal.generated.js";

const EXPRESSION_SOURCES: ExpressionItemSource[] = ["self"];

interface FormValues {
  bindProps: {
    destKey: string;
    sourceKey: string;
  }[];
  includeIfExpr: VariableInputState;
  eligibleIfExpr: VariableInputState;
}

interface Props {
  docId: string;
  docType: DocType;
  itemId: string;
  recommendation: EditRecommendedTemplateModal_ItemRecommendationFragment;
  recommendationConfig: EditRecommendedTemplateModal_ItemRecommendationConfigFragment;
  recommendedTemplateConfig: EditRecommendedTemplateModal_RecommendedTemplateConfigFragment;
  allowedItemTypes: ItemType[];
  handleClose(): void;
  handleComplete(
    values: ModifyItemRecommendedTemplateValuesInput | null
  ): Promise<void> | void;
  documentViewerPermissions: PermissionName[];
}

export const EditRecommendedTemplateModal = ({
  docId,
  itemId,
  recommendation,
  recommendationConfig,
  recommendedTemplateConfig,
  allowedItemTypes,
  handleClose,
  handleComplete,
  docType,
  documentViewerPermissions,
}: Props) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const { isMaxPhone } = useScreenWidth();
  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-recommended-template-modal-fullscreen",
    false,
    true
  );

  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const query = useEditRecommendedTemplateModalQuery({
    client,
    variables: {
      itemId,
      docId,
      templateId: recommendedTemplateConfig.templateId,
      templateVersionNumber: recommendedTemplateConfig.templateVersionNumber,
      applyOverrides: false,
    },
  });
  const queryLatestAvailableVersion =
    useEditRecommendedTemplateModal__LatestAvailableVersionQuery({
      client,
      variables: {
        templateId: recommendedTemplateConfig.templateId,
      },
      fetchPolicy: "no-cache",
    });

  const templateLatestAvailableVersion = getDataOrNull(
    queryLatestAvailableVersion.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate?.resolvedAsReadModelVersionNumber;

  const templateLatestAvailableVersionRootProps = (
    getDataOrNull(
      queryLatestAvailableVersion.data?.quoteTemplateLatestAvailableVersion
    )?.quoteTemplate?.rootItem?.props2 ?? []
  ).filter(isNonComputedProp);
  const templateLatestAvailableVersionRootType = getDataOrNull(
    queryLatestAvailableVersion.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate?.rootItem?.type;

  const recommendedTemplateRootProps = (
    getDataOrNull(query.data?.quoteTemplateVersion)?.quoteTemplate?.rootItem
      ?.props2 ?? []
  ).filter(isNonComputedProp);
  const sourceProps = getDataOrNull(query.data?.item)?.props2 ?? [];

  const getPropertyOption = (prop: Props2) => ({
    value: prop.key,
    label: getEnhancedPropertyLabel(prop, false),
  });

  const recommendedTemplateRootPropsOptions =
    recommendedTemplateRootProps.map(getPropertyOption);

  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const sourcePropsOptions = (recommendedPropKey: string) => {
    const recommendedProp = recommendedTemplateRootProps.find(
      p => p.key === recommendedPropKey
    );
    if (!recommendedProp) return [];

    const propType = getPropertyType(recommendedProp);
    const propIsArray = isArrayType(recommendedProp);
    switch (propType) {
      case "BOOLEAN":
        return sourceProps.filter(isBooleanType).map(getPropertyOption);
      case "TEXT":
        return sourceProps
          .filter(isTextType)
          .filter(propIsArray ? isArrayType : isNotArrayType)
          .map(getPropertyOption);
      case "NUMBER":
        return sourceProps
          .filter(isNumberType)
          .filter(propIsArray ? isArrayType : isNotArrayType)
          .map(getPropertyOption);
    }
  };

  const { expressionCheckResults, validateExpressions } =
    useExpressionValidation(docId, itemId);
  const eligibleIfExprError = expressionCheckResults?.find(
    value =>
      value.__typename ===
        "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf" &&
      value.itemRecommendationId === recommendation.id
  );
  const includeIfExprError = expressionCheckResults?.find(
    value =>
      value.__typename ===
        "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf" &&
      value.itemRecommendationId === recommendation.id
  );

  const initialValues: FormValues = {
    bindProps: recommendedTemplateConfig.bindProps,
    includeIfExpr: {
      expression: recommendationConfig.includeIfExpr,
      cursorIndex: null,
    },
    eligibleIfExpr: {
      expression: recommendationConfig.eligibleIfExpr,
      cursorIndex: null,
    },
  };

  const formId = React.useMemo(() => uniqueId(), []);

  const validationSchema = Yup.object().shape({
    includeIfExpr: Yup.object().required().shape({
      expression: Yup.string(),
      cursorIndex: Yup.number().nullable(),
    }),
    eligibleIfExpr: Yup.object().required().shape({
      expression: Yup.string(),
      cursorIndex: Yup.number().nullable(),
    }),
    bindProps: Yup.array().of(
      Yup.object().shape({
        destKey: Yup.string()
          .required()
          .label(
            t("Recommended template property", {
              ns: "QuoteItem",
            })
          )
          .test(
            "unique",
            t("Value must be unique", {
              ns: "Global",
            }),
            function (value: string | undefined) {
              // @ts-ignore
              const bindProps = (this.from as any)[1].value.bindProps as {
                destKey: string;
              }[];
              const sameValues = bindProps.filter(p => p.destKey === value);
              return sameValues.length <= 1;
            }
          ),
        sourceKey: Yup.string()
          .required()
          .label(
            t("Source item property", {
              ns: "QuoteItem",
            })
          ),
      })
    ),
  });

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async values => {
        const result = await validateExpressions({
          docId,
          itemId,
          overrides: [
            {
              itemId,
              recommended: {
                eligibleIf: values.eligibleIfExpr.expression,
                includeIf: values.includeIfExpr.expression,
                recommendationId: recommendation.id,
              },
            },
          ],
        });

        const expressionCheckResult = getDataOrNull(
          result.data?.compileDocIsolatedExpression
        )?.results?.filter(
          result =>
            (result.__typename ===
              "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf" ||
              result.__typename ===
                "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf") &&
            result.itemRecommendationId === recommendation.id
        );

        if (!expressionCheckResult) {
          console.error("compile document error");
          enqueueSnackbar(`Cannot compile expressions! Not allowed`, {
            variant: "error",
          });
          return;
        }
        if (expressionCheckResult.length > 0) {
          console.error("compile document error", expressionCheckResult);
          expressionCheckResult.forEach(result => {
            enqueueSnackbar(`Expression is invalid: ${result.messageText}`, {
              variant: "error",
            });
          });
          return;
        }

        await handleComplete({
          templateVersionNumber:
            recommendedTemplateConfig.templateVersionNumber,
          bindProps: values.bindProps.map(p => ({
            destKey: p.destKey,
            sourceKey: p.sourceKey,
          })),
          includeIfExpr: values.includeIfExpr.expression,
          eligibleIfExpr: values.eligibleIfExpr.expression,
        });
        handleClose();
      }}
    >
      {formikProps => (
        <Modal
          maxWidth="md"
          title={t("Configure template {templateName}", {
            ns: "QuoteItem",
            templateName: recommendation.teaserTitle,
          })}
          handleClose={handleClose}
          dialogProps={
            !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
          }
          headerActions={
            !isMaxPhone ? (
              <FullScreenToggleIconButton
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
              />
            ) : undefined
          }
          alwaysVisible
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Remove template", { ns: "QuoteItem" }),
              handleClick: async () => {
                if (isDeleting) return;
                const result = await startConfirmProcess({
                  title: t("Confirm deletion", { ns: "Global" }),
                  text: t(
                    "Are you sure you would like to delete this recommended template?",
                    {
                      ns: "QuoteItem",
                    }
                  ),
                  confirmButtonLabel: t("Delete", { ns: "Global" }),
                });
                if (!result) return;
                setIsDeleting(true);
                try {
                  await handleComplete(null);
                } finally {
                  setIsDeleting(false);
                }
                handleClose();
              },
              buttonProps: {
                variant: "outlined",
                disabled: formikProps.isSubmitting || isDeleting,
              },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack direction="column" spacing={2}>
              {templateLatestAvailableVersion &&
                templateLatestAvailableVersionRootType &&
                templateLatestAvailableVersion >
                  recommendedTemplateConfig.templateVersionNumber && (
                  <Box>
                    <Button
                      color="primary"
                      variant="contained"
                      size="small"
                      onClick={async () => {
                        if (
                          !allowedItemTypes.includes(
                            templateLatestAvailableVersionRootType
                          )
                        ) {
                          enqueueSnackbar(
                            t(
                              "Root item type in the newer version is no longer compatible",
                              {
                                ns: "QuoteItem",
                              }
                            ),
                            { variant: "error" }
                          );
                          return;
                        }
                        if (isUpdating) return;
                        setIsUpdating(true);
                        try {
                          await handleComplete({
                            includeIfExpr:
                              formikProps.values.includeIfExpr.expression,
                            eligibleIfExpr:
                              formikProps.values.eligibleIfExpr.expression,
                            templateVersionNumber:
                              templateLatestAvailableVersion,
                            bindProps:
                              recommendedTemplateConfig.bindProps.filter(
                                ({ destKey }) =>
                                  templateLatestAvailableVersionRootProps.find(
                                    p => p.key === destKey
                                  )
                              ),
                          });
                          enqueueSnackbar(
                            t("Template was updated to the latest version", {
                              ns: "QuoteItem",
                            })
                          );
                        } finally {
                          setIsUpdating(false);
                        }
                      }}
                      loading={isUpdating}
                    >
                      {t("Update to latest version", { ns: "QuoteItem" })}
                    </Button>
                  </Box>
                )}
              <CollapseSection
                title={t("Rules", { ns: "QuoteItem" })}
                isInitiallyExpanded
              >
                <Stack direction="column" spacing={1}>
                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                    capability="EXPRESSIONS"
                    permissionByType={{
                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                      REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                    }}
                    document={document}
                    otherwise={
                      <ExpressionInputWithVariables
                        label={t("Eligible if", { ns: "QuoteItem" })}
                        placeholder={t("Formula", { ns: "QuoteItem" })}
                        itemId={itemId}
                        docId={docId}
                        projectId={null}
                        inputState={formikProps.values.eligibleIfExpr}
                        disabled={true}
                        direction="row"
                        onInputStateChange={noop}
                      />
                    }
                  >
                    <ExpressionInputWithVariables
                      label={t("Eligible if", { ns: "QuoteItem" })}
                      placeholder={t("Formula", { ns: "QuoteItem" })}
                      itemId={itemId}
                      docId={docId}
                      projectId={null}
                      expressionError={
                        eligibleIfExprError
                          ? new Error(eligibleIfExprError.messageText)
                          : null
                      }
                      inputState={formikProps.values.eligibleIfExpr}
                      onInputStateChange={expr => {
                        formikProps.setFieldValue("eligibleIfExpr", expr);
                      }}
                      autoFocus={false}
                      direction="row"
                      allowedSources={EXPRESSION_SOURCES}
                      showSelectedVariableAllowedValues={true}
                    />
                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                    capability="EXPRESSIONS"
                    permissionByType={{
                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                      REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                    }}
                    document={document}
                    otherwise={
                      <ExpressionInputWithVariables
                        label={t("Include if", { ns: "QuoteItem" })}
                        placeholder={t("Formula", { ns: "QuoteItem" })}
                        itemId={itemId}
                        docId={docId}
                        projectId={null}
                        inputState={formikProps.values.includeIfExpr}
                        disabled={true}
                        direction="row"
                        onInputStateChange={noop}
                      />
                    }
                  >
                    <ExpressionInputWithVariables
                      label={t("Include if", { ns: "QuoteItem" })}
                      placeholder={t("Formula", { ns: "QuoteItem" })}
                      itemId={itemId}
                      docId={docId}
                      projectId={null}
                      expressionError={
                        includeIfExprError
                          ? new Error(includeIfExprError.messageText)
                          : null
                      }
                      inputState={formikProps.values.includeIfExpr}
                      onInputStateChange={expr => {
                        formikProps.setFieldValue("includeIfExpr", expr);
                      }}
                      autoFocus={false}
                      direction="row"
                      allowedSources={EXPRESSION_SOURCES}
                      showSelectedVariableAllowedValues={true}
                    />
                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                </Stack>
              </CollapseSection>
              <CollapseSection
                title={t("Linked properties", { ns: "QuoteItem" })}
                isInitiallyExpanded
                disableAutoExpand
                itemCount={formikProps.values.bindProps.length}
              >
                <FieldArray
                  name="bindProps"
                  render={arrayHelpers => (
                    <Stack direction="column" spacing={1}>
                      {formikProps.values.bindProps.map((bindProp, index) => (
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                            }}
                          >
                            <SelectField
                              name={`bindProps[${index}].destKey`}
                              required
                              disabled={formikProps.isSubmitting}
                              options={recommendedTemplateRootPropsOptions}
                              label={t("Recommended template property", {
                                ns: "QuoteItem",
                              })}
                              // placeholder={t("Select property", {
                              //   ns: "QuoteItem",
                              // })}
                              onChange={propKey => {
                                if (!propKey) return;
                                arrayHelpers.replace(index, {
                                  destKey: propKey,
                                  sourceKey: "",
                                });
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              flex: 1,
                            }}
                          >
                            <SelectField
                              name={`${arrayHelpers.name}[${index}].sourceKey`}
                              required
                              disabled={formikProps.isSubmitting}
                              options={
                                formikProps.values.bindProps[index].destKey
                                  ? sourcePropsOptions(
                                      formikProps.values.bindProps[index]
                                        .destKey
                                    )
                                  : []
                              }
                              label={t("Source item property", {
                                ns: "QuoteItem",
                              })}
                              // placeholder={
                              //   formikProps.values.bindProps[index].destKey
                              //     ? t("Select property", {
                              //         ns: "QuoteItem",
                              //       })
                              //     : t("Select recommended property first", {
                              //         ns: "QuoteItem",
                              //       })
                              // }
                              onChange={propKey => {
                                if (!propKey) return;
                                arrayHelpers.replace(index, {
                                  destKey:
                                    formikProps.values.bindProps[index].destKey,
                                  sourceKey: propKey,
                                });
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              flex: 0,
                            }}
                          >
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                arrayHelpers.remove(index);
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Box>
                        </Stack>
                      ))}
                      <Box>
                        <Button
                          color="secondary"
                          variant="text"
                          size="extra-small"
                          onClick={() => {
                            arrayHelpers.push({
                              destKey: "",
                              sourceKey: "",
                            });
                          }}
                          startIcon={<AddIcon />}
                        >
                          {t("Add property mapping", { ns: "QuoteItem" })}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                />
              </CollapseSection>
            </Stack>
          </Form>
          <ConfirmProcess ref={confirmProcessRef} />
        </Modal>
      )}
    </Formik>
  );
};
